// ── File System Access API Integration ──────────────────────────────────
// Provides opt-in local folder storage for benchmark and trend results.
// Only supported in Chromium-based browsers (Chrome, Edge).

import { sanitizeForExport } from './security-utils'

/** In-memory handle to the user-selected directory */
let directoryHandle: any | null = null

/**
 * Checks if the browser supports the File System Access API
 * (window.showDirectoryPicker). Returns false for Firefox/Safari.
 */
export function isFileSystemAccessSupported(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window
}

/**
 * Prompts the user to select a folder via the File System Access API.
 * Stores the handle in memory for subsequent read/write operations.
 * Throws if the user cancels or the browser doesn't support the API.
 */
export async function selectFolder(): Promise<boolean> {
  if (!isFileSystemAccessSupported()) {
    throw new Error('File System Access API is not supported in this browser')
  }
  try {
    directoryHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' })
    return true
  } catch (err: any) {
    // User cancelled the picker
    if (err?.name === 'AbortError') {
      return false
    }
    throw err
  }
}

/**
 * Returns true if a folder handle is currently held in memory.
 */
export function hasFolderAccess(): boolean {
  return directoryHandle !== null
}

/**
 * Writes a sanitized JSON file to the selected folder.
 * Strips all credential/API key fields before writing.
 * @param filename - The file name (e.g., "benchmark-run-abc123.json")
 * @param data - The data object to serialize and save
 */
export async function saveResultToFolder(filename: string, data: unknown): Promise<void> {
  if (!directoryHandle) {
    throw new Error('No folder selected. Call selectFolder() first.')
  }
  // Sanitize to remove any credential data before writing to disk
  const sanitized = sanitizeForExport(data)
  const json = JSON.stringify(sanitized, null, 2)

  const fileHandle = await directoryHandle.getFileHandle(filename, { create: true })
  const writable = await fileHandle.createWritable()
  await writable.write(json)
  await writable.close()
}

/**
 * Reads all JSON files from the selected folder and returns their parsed contents.
 * Skips files that fail to parse.
 */
export async function readResultsFromFolder(): Promise<unknown[]> {
  if (!directoryHandle) {
    return []
  }
  const results: unknown[] = []
  for await (const [name, handle] of directoryHandle.entries()) {
    if (handle.kind === 'file' && name.endsWith('.json')) {
      try {
        const file = await handle.getFile()
        const text = await file.text()
        results.push(JSON.parse(text))
      } catch {
        // Skip files that can't be read or parsed
        console.warn(`[fs-storage] Skipping unreadable file: ${name}`)
      }
    }
  }
  return results
}

/**
 * Releases the folder handle and stops all file operations.
 * Called when the user disables local folder storage or closes the tab.
 */
export function releaseFolder(): void {
  directoryHandle = null
}

/**
 * Registers a beforeunload handler to release the folder handle on tab close.
 * Should be called once during app initialization when folder storage is enabled.
 */
export function registerCleanupOnUnload(): void {
  window.addEventListener('beforeunload', () => {
    releaseFolder()
  })
}
